import _, { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { DeleteAutomaticUserStateCommand, SetAutomaticUserStateCommand } from 'src/stores/userState/commands';
import { SUBSCRIBED_AUTOMATIC_USER_STATES } from 'src/stores/userState/localStorageKeys';
import { GetAutomaticUserStatesQuery } from 'src/stores/userState/queries';

import { AutomaticUserState, AutomaticUserStateRunType } from 'src/models/userState';

import { ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY } from 'src/services/authentication';

import { GetAutomaticUserStatesCallBack } from './callbacks';
import {
	DeleteActiveAutomaticUserStatePayload,
	FetchAutomaticUserStatesPayload,
	SetActiveAutomaticUserStatePayload,
	SetPreviousActiveAutomaticUserStatePayload,
	SetPreviousAutomaticUserStatesPayload,
	SetSubscribedAutomaticUserStatesPayload,
	UserStateStoreState,
} from './interfaces';

const storageKeyForSubscribedAutomaticUserStates = (): string => {
	const instanceName = (localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY) as string) ?? '';
	return `${SUBSCRIBED_AUTOMATIC_USER_STATES}_${instanceName}`;
};

const readSubscribedAutomaticUserStatesFromStorage = (): Array<AutomaticUserState> => {
	const serializedSubscribedAutomaticUserStates = localStorage.getItem(storageKeyForSubscribedAutomaticUserStates());

	if (serializedSubscribedAutomaticUserStates === null) {
		return [];
	}

	return JSON.parse(serializedSubscribedAutomaticUserStates) as Array<AutomaticUserState>;
};

const createDefaultState = (): UserStateStoreState => ({
	activeAutomaticUserState: null,
	automaticUserStates: [],
	previousActiveAutomaticUserState: null,
	previousAutomaticUserStates: [],
	subscribedAutomaticUserStates: readSubscribedAutomaticUserStatesFromStorage(),
});

export const useUserStateStore = defineStore('userState', {
	actions: {
		deleteActiveAutomaticUserState(payload: DeleteActiveAutomaticUserStatePayload): void {
			void payload.commandBus
				?.sendCommand(new DeleteAutomaticUserStateCommand(payload.activeAutomaticUserState?.id))
				.then(() => {
					this.activeAutomaticUserState = null;
				});
		},
		fetchAutomaticUserStates(payload: FetchAutomaticUserStatesPayload): void {
			if (payload.queryStore !== undefined) {
				void payload.queryStore.query<AutomaticUserState[]>({
					callback: new GetAutomaticUserStatesCallBack((automaticUserStates) =>
						this.setAutomaticUserStates(automaticUserStates)
					),
					createTime: new Date(),
					query: new GetAutomaticUserStatesQuery(),
					responseType: 'json',
					runType: AutomaticUserStateRunType,
				});
				return;
			}

			this.setAutomaticUserStates([]);
		},
		setActiveAutomaticUserState(payload: SetActiveAutomaticUserStatePayload): void {
			void payload.commandBus
				?.sendCommand(new SetAutomaticUserStateCommand(payload.activeAutomaticUserState.id))
				.then(() => {
					this.activeAutomaticUserState = payload.activeAutomaticUserState;
				});
		},
		setAutomaticUserStates(payload: Array<AutomaticUserState>): void {
			this.automaticUserStates = payload;

			let updatedAutomaticUserStates: Array<AutomaticUserState> = cloneDeep(this.subscribedAutomaticUserStates);

			this.subscribedAutomaticUserStates.forEach((subscribedAutomaticUserState) => {
				const automaticUserState = payload.find(
					(candidateAutomaticUserState) => candidateAutomaticUserState.id === subscribedAutomaticUserState.id
				);

				if (automaticUserState !== undefined) {
					if (!_.isEqual(subscribedAutomaticUserState, automaticUserState)) {
						const subscribedAutomaticUserStateIndex = updatedAutomaticUserStates.findIndex(
							(subscribedAutomaticUserStateToReplace) =>
								subscribedAutomaticUserStateToReplace.id === subscribedAutomaticUserState.id
						);

						if (
							subscribedAutomaticUserState.longitude === automaticUserState.longitude &&
							subscribedAutomaticUserState.latitude === automaticUserState.latitude
						) {
							updatedAutomaticUserStates[subscribedAutomaticUserStateIndex] = automaticUserState;
						} else {
							updatedAutomaticUserStates = updatedAutomaticUserStates.filter(
								(candidateAutomaticUserState) =>
									candidateAutomaticUserState.id !== subscribedAutomaticUserState.id
							);
						}
					}
				} else {
					updatedAutomaticUserStates = updatedAutomaticUserStates.filter(
						(candidateAutomaticUserState) =>
							candidateAutomaticUserState.id !== subscribedAutomaticUserState.id
					);
				}
			});

			if (!_.isEqual(updatedAutomaticUserStates, this.subscribedAutomaticUserStates)) {
				this.subscribedAutomaticUserStates = updatedAutomaticUserStates;

				localStorage.setItem(
					storageKeyForSubscribedAutomaticUserStates(),
					JSON.stringify(updatedAutomaticUserStates)
				);
			}
		},
		setPreviousActiveAutomaticUserState(payload: SetPreviousActiveAutomaticUserStatePayload): void {
			this.previousActiveAutomaticUserState = payload.previousActiveAutomaticUserState;
		},
		setPreviousAutomaticUserStates(payload: SetPreviousAutomaticUserStatesPayload): void {
			this.previousAutomaticUserStates = payload.previousAutomaticUserStates;
		},
		setSubscribedAutomaticUserStates(payload: SetSubscribedAutomaticUserStatesPayload): void {
			localStorage.setItem(
				storageKeyForSubscribedAutomaticUserStates(),
				JSON.stringify(payload.subscribedAutomaticUserStates)
			);

			this.subscribedAutomaticUserStates = payload.subscribedAutomaticUserStates;
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
